import { useEffect, useState } from "react";

import { useRouter } from "next/router";

import { useSolutionShowcaseExperiment } from "@/features/Auth/Register/components/SolutionShowcase/helpers/useSolutionShowcaseExperiment";
import { RegisterCategoryResolver } from "@/features/Auth/Register/helpers/RegisterCategoryResolver";
import { RegisterCategory } from "@/features/Auth/Register/types";

/**
 * Register landing page.
 *
 * Resolves the category from query params, then resolves the
 * register_solution_showcase experiment (desktop + US + EN) and redirects to
 * the matching SSG'd page: /register/b/method-choice/{category} for the test
 * variant, /register/method-choice/{category} otherwise. The page renders
 * nothing, so deciding here costs a short blank instead of a layout flash.
 * All incoming query parameters are preserved so analytics/attribution stay
 * intact on the destination.
 *
 * Query params are read from window.location: on this statically generated
 * route router.query (and thus useSearchParams) can miss the search params
 * on the first renders after the locale redirect.
 */
const RegisterLanding = () => {
  const router = useRouter();
  const [category, setCategory] = useState<RegisterCategory | undefined>(undefined);

  useEffect(() => {
    setCategory(RegisterCategoryResolver.resolveCategory(new URLSearchParams(window.location.search)));
  }, []);

  const { showShowcase, isResolved } = useSolutionShowcaseExperiment("method-choice", category);

  useEffect(() => {
    if (!category || !isResolved) {
      return;
    }

    const search = window.location.search;
    const basePath = showShowcase ? "/register/b/method-choice" : "/register/method-choice";

    void router.replace(`${basePath}/${category}${search}`);
  }, [category, isResolved, showShowcase, router]);

  return null;
};

export default RegisterLanding;
